import type { HeroVariant } from "./hero-variants";

import { trackClickEvent } from "@homeinstead/analytics";
import { getWhatsNextToolkitUrl } from "@homeinstead/locale";
import { scrollToTop } from "@homeinstead/shared";
import { cn } from "@homeinstead/ui";
import DecorativeStars from "@homeinstead/ui/components/animations/DecorativeStars";
import SpinningStar from "@homeinstead/ui/components/animations/SpinningStars";
import { Button } from "@homeinstead/ui/components/Button";
import { useEffect } from "react";
import { useLocale } from "~www/components/LocaleProvider";
import { getHeroVariantStyles } from "./hero-variants";

const ThankYou = ({
  firstName,
  variant = "default",
}: {
  firstName: string;
  variant?: HeroVariant;
}) => {
  const locale = useLocale();
  const whatsNextToolkitUrl = getWhatsNextToolkitUrl(locale.metadata.code);

  function trackToolkitDownload() {
    trackClickEvent("rich_text_link", { target_url: whatsNextToolkitUrl });
  }

  const { textColorClass, textPositionClass, isLight } =
    getHeroVariantStyles(variant);

  useEffect(() => {
    // just ensures the thank you page is scrolled to the top
    scrollToTop();
  }, []);

  return (
    <div
      className={cn(
        "flex w-full flex-col items-center gap-4 px-6 font-jakarta",
        textColorClass,
        textPositionClass,
      )}
    >
      <div className="relative mt-12">
        <SpinningStar
          checkClassName={isLight ? "stroke-new-beginnings-base" : ""}
          className={isLight ? "fill-white" : ""}
        />
        <DecorativeStars className={isLight ? "fill-white" : ""} />
      </div>
      <div className="flex max-w-[560px] flex-col items-start pb-20">
        <h1
          className={cn(
            "mb-4 font-bold font-jakarta md:mb-2",
            "text-xl md:text-1xl",
          )}
        >
          We&apos;ve received your details, {firstName}.
        </h1>
        <p className="mb-10 text-md leading-relaxed">
          A care team member from your local Home Instead® office will be
          getting in touch with you shortly.
        </p>

        <p className="mb-10 text-md leading-relaxed">
          Our home care plans are meant to ensure both comfort and safety. If
          your needs change, our care plans can be adjusted accordingly.
        </p>

        {!whatsNextToolkitUrl ? null : (
          <Button
            asChild
            className="w-full md:w-auto"
            onClick={trackToolkitDownload}
          >
            <a
              href={whatsNextToolkitUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              Download Our &ldquo;What&apos;s Next&rdquo; Toolkit
            </a>
          </Button>
        )}
      </div>
    </div>
  );
};

export default ThankYou;
